/**
 * Salesforce 기회 상품 대량 업로드 API 클라이언트.
 *   GET  /api/salesforce/status
 *   POST /api/salesforce/opp-products/preview?template=  (raw xlsx body → dry-run)
 *   POST /api/salesforce/opp-products/commit?template=   (raw xlsx body → create)
 * 업로드는 raw body(application/octet-stream) — 서버 라우터와 동일 규약.
 */
export class SalesforceError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'SalesforceError'
    this.status = status
  }
}

export interface SalesforceTemplate {
  key: string
  label: string
  description: string
  requiredColumns: string[]
}

export interface SalesforceStatus {
  configured: boolean
  templates: SalesforceTemplate[]
}

export interface PreviewRow {
  rowNum: number
  opportunityId: string
  status: 'ok' | 'error'
  errors: string[]
  payload: Record<string, unknown>
}
export interface PreviewResult {
  template: string
  summary: { total: number; ok: number; error: number }
  rows: PreviewRow[]
}

export interface CommitRow {
  rowNum: number
  opportunityId: string
  status: 'created' | 'failed' | 'error'
  errors: string[]
  id?: string
  payload: Record<string, unknown>
}
export interface CommitResult {
  template: string
  summary: { total: number; created: number; failed: number; skipped: number }
  rows: CommitRow[]
}

async function postFile<T>(path: string, file: File): Promise<T> {
  const buf = await file.arrayBuffer()
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: buf,
  })
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    /* non-JSON */
  }
  if (!res.ok) {
    const code = (body as { error?: string } | null)?.error ?? `요청 실패 (${res.status})`
    throw new SalesforceError(code, res.status)
  }
  return body as T
}

export async function getSalesforceStatus(): Promise<SalesforceStatus> {
  const res = await fetch('/api/salesforce/status', { credentials: 'same-origin' })
  if (!res.ok) throw new SalesforceError(`status_${res.status}`, res.status)
  return (await res.json()) as SalesforceStatus
}

/** 업로드 파일의 시트 이름 목록 (여러 탭 워크북 대응). */
export async function getSheetNames(file: File): Promise<string[]> {
  const r = await postFile<{ sheets: string[] }>('/api/salesforce/opp-products/sheets', file)
  return r.sheets ?? []
}

function qs(template: string, sheet?: string): string {
  const p = new URLSearchParams({ template })
  if (sheet) p.set('sheet', sheet)
  return p.toString()
}

export function previewOppProducts(
  template: string,
  file: File,
  sheet?: string,
): Promise<PreviewResult> {
  return postFile<PreviewResult>(`/api/salesforce/opp-products/preview?${qs(template, sheet)}`, file)
}

export function commitOppProducts(
  template: string,
  file: File,
  sheet?: string,
): Promise<CommitResult> {
  return postFile<CommitResult>(`/api/salesforce/opp-products/commit?${qs(template, sheet)}`, file)
}

/** 서버 error 코드 → 한국어 메시지. */
export function humanizeSalesforceError(code: string): string {
  switch (code) {
    case 'salesforce_not_configured':
      return 'Salesforce 자격증명이 서버에 설정되지 않았어요. (.env / Render env)'
    case 'salesforce_login_failed':
      return 'Salesforce 로그인 실패. 자격증명 또는 보안 토큰을 확인해 주세요.'
    case 'unknown_template':
      return '알 수 없는 템플릿이에요.'
    case 'empty_upload':
      return '업로드된 파일이 비어 있어요.'
    case 'empty_sheet':
      return '시트에 데이터 행이 없어요.'
    case 'parse_failed':
      return '엑셀 파싱 실패. .xlsx 또는 .csv 파일인지 확인해 주세요.'
    default:
      return code.startsWith('요청 실패') ? code : `오류 (${code})`
  }
}
